/**
 * The platform console's "Consultant limits" panel (migration 0201): per org, the AI Casting Consultant's daily limits
 * with where each comes from (the platform's default, the server's setting, or the org's own), editable; today's usage
 * (the org's count and the people with the most messages); the latest resets; and the resets themselves, "Reset for
 * this org" and "Reset for <person>", each asked for in the page before it is sent (never window.confirm, which some
 * views block). Only a platform admin sees any of it: the console redirects everyone else, and the cell and the sheet
 * render nothing for them besides; the routes are RequirePlatformAdmin's.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge, Loader2, RotateCcw } from "lucide-react";
import { platformAdminApi, type CopilotLimits, type OrgSummary } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import {
  copilotLimitsKey,
  formOfLimits,
  limitWords,
  limitsBody,
  limitsChanged,
  limitsErrorWords,
  personName,
  platformPlaceholder,
  resetButtonWords,
  resetConfirmWords,
  resetDoneWords,
  resetLineWords,
  usageWords,
  type LimitsForm,
  type ResetTarget,
} from "./copilot-limits";

/** The Consultant column's cell: opens the org's limits. Nothing for anyone but a platform admin. */
export function ConsultantLimitsCell({ org, onOpen }: { org: Pick<OrgSummary, "name">; onOpen: () => void }) {
  const { isPlatformAdmin } = useOrg();
  if (!isPlatformAdmin) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-2 text-xs"
      title={`Consultant limits for ${org.name}`}
      aria-label={`Consultant limits for ${org.name}`}
      onClick={onOpen}
    >
      <Gauge className="size-3.5" />
      Limits
    </Button>
  );
}

export interface ConsultantLimitsViewProps {
  orgName: string;
  view: CopilotLimits;
  form: LimitsForm;
  onFormChange: (f: LimitsForm) => void;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
  /** The last refusal or failure, in words. */
  error: string | null;
  note: string;
  onNoteChange: (note: string) => void;
  /** The reset waiting for its confirmation, or none. */
  confirm: ResetTarget | null;
  onAskReset: (t: ResetTarget) => void;
  onConfirmReset: () => void;
  onCancelReset: () => void;
  resetting: boolean;
  /** What the panel says once a reset is made. */
  resetDone: string | null;
  onClose: () => void;
}

/** The panel, pure: exported for the tests. */
export function ConsultantLimitsView(p: ConsultantLimitsViewProps) {
  const v = p.view;
  const busy = p.saving || p.resetting;
  const asking = p.confirm !== null;
  return (
    <div className="flex flex-col gap-5 p-5">
      <p className="text-sm text-on-surface-muted">
        The AI Casting Consultant takes a set number of messages in any 24 hours from each person of {p.orgName}, and
        from the whole org. Set this org&apos;s own limits here, or start a count again with a reset. A reset never
        deletes a message.
      </p>

      <form
        className="flex flex-col gap-3"
        aria-label="Limits"
        onSubmit={(e) => {
          e.preventDefault();
          p.onSave();
        }}
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">Limits</h3>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="copilot-user-daily" className="font-medium text-on-surface">
            Per person, a day
          </label>
          <p data-limit="user" className="text-xs text-on-surface-muted">
            Now: {limitWords(v.user_daily)}
          </p>
          <Input
            id="copilot-user-daily"
            name="user_daily"
            inputMode="numeric"
            value={p.form.user}
            placeholder={platformPlaceholder(v.user_daily)}
            onChange={(e) => p.onFormChange({ ...p.form, user: e.target.value })}
            disabled={busy}
          />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="copilot-org-daily" className="font-medium text-on-surface">
            Whole org, a day
          </label>
          <p data-limit="org" className="text-xs text-on-surface-muted">
            Now: {limitWords(v.org_daily)}
          </p>
          <Input
            id="copilot-org-daily"
            name="org_daily"
            inputMode="numeric"
            value={p.form.org}
            placeholder={platformPlaceholder(v.org_daily)}
            onChange={(e) => p.onFormChange({ ...p.form, org: e.target.value })}
            disabled={busy}
          />
        </div>
        <p className="text-xs text-on-surface-muted">Leave a field empty to use the platform&apos;s limit.</p>
        <div className="flex items-center justify-end gap-2">
          {p.saved && (
            <span role="status" className="text-sm text-success">
              Saved.
            </span>
          )}
          <Button type="submit" variant="primary" size="sm" disabled={busy || !limitsChanged(p.form, v)}>
            {p.saving && <Loader2 className="mr-1.5 size-4 animate-spin" />}
            Save limits
          </Button>
        </div>
      </form>

      <section className="flex flex-col gap-3" aria-label="Usage">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">Usage</h3>
        <div className="flex items-center justify-between gap-3 rounded-md bg-surface-muted px-3 py-2 text-sm">
          <span data-usage="org" className="text-on-surface">
            The org: {usageWords(v.org_count, v.org_daily.limit, v.org_since, v.now)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || asking}
            onClick={() => p.onAskReset({ kind: "org" })}
          >
            <RotateCcw className="mr-1 size-3.5" />
            {resetButtonWords({ kind: "org" })}
          </Button>
        </div>
        {v.people.length === 0 ? (
          <p data-usage="none" className="text-sm text-on-surface-muted">
            Nobody has a message counted right now.
          </p>
        ) : (
          <ul className="flex flex-col gap-1" aria-label="People">
            {v.people.map((u) => {
              const target: ResetTarget = { kind: "person", userId: u.user_id, name: personName(u) };
              return (
                <li
                  key={u.user_id}
                  data-person={u.user_id}
                  className="flex items-center justify-between gap-3 rounded-md px-3 py-1.5 text-sm"
                >
                  <span className="min-w-0 truncate text-on-surface">
                    {personName(u)}
                    {u.display_name.trim() ? <span className="text-on-surface-muted"> ({u.email})</span> : null}
                    <span className="block text-xs text-on-surface-muted">
                      {usageWords(u.count, v.user_daily.limit, u.since, v.now)}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy || asking}
                    onClick={() => p.onAskReset(target)}
                  >
                    {resetButtonWords(target)}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="copilot-reset-note" className="font-medium text-on-surface">
            Why (optional, kept with the reset)
          </label>
          <Input
            id="copilot-reset-note"
            name="note"
            maxLength={500}
            value={p.note}
            onChange={(e) => p.onNoteChange(e.target.value)}
            disabled={busy}
          />
        </div>
        {p.confirm && (
          <div
            role="alertdialog"
            aria-label="Confirm the reset"
            className="flex flex-col gap-3 rounded-md border border-warning/40 bg-warning/10 p-3"
          >
            <p className="text-sm text-on-surface">{resetConfirmWords(p.confirm, p.orgName)}</p>
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={p.onCancelReset} disabled={p.resetting}>
                Keep the count
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={p.onConfirmReset} disabled={p.resetting}>
                {p.resetting && <Loader2 className="mr-1.5 size-4 animate-spin" />}
                Reset the count
              </Button>
            </div>
          </div>
        )}
        {p.resetDone && (
          <p role="status" className="rounded-md bg-success/10 px-3 py-2 text-sm text-on-surface">
            {p.resetDone}
          </p>
        )}
      </section>

      {p.error && (
        <p role="alert" className="text-sm text-danger">
          {p.error}
        </p>
      )}

      <section className="flex flex-col gap-2" aria-label="Latest resets">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">Latest resets</h3>
        {v.resets.length === 0 ? (
          <p data-resets="none" className="text-sm text-on-surface-muted">
            No count of {p.orgName} has been reset.
          </p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-on-surface">
            {v.resets.map((r) => (
              <li key={r.id} data-reset={r.id}>
                {resetLineWords(r)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={p.onClose} disabled={busy}>
          Close
        </Button>
      </div>
    </div>
  );
}

/** The panel's state and its calls: the form starts from the stored limits, a save or a reset hands back the view. */
function ConsultantLimitsEditor({
  org,
  view,
  onView,
  onClose,
}: {
  org: Pick<OrgSummary, "id" | "name">;
  view: CopilotLimits;
  onView: (v: CopilotLimits) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<LimitsForm>(() => formOfLimits(view));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState<ResetTarget | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState<string | null>(null);

  const save = async () => {
    const made = limitsBody(form);
    if ("error" in made) {
      setError(made.error);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const next = await platformAdminApi.putOrgCopilotLimits(org.id, made.body);
      onView(next);
      setForm(formOfLimits(next));
      setSaved(true);
    } catch (err) {
      setError(limitsErrorWords(err, org.name));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm) return;
    const target = confirm;
    setError(null);
    setResetting(true);
    try {
      const next = await platformAdminApi.resetOrgCopilotQuota(org.id, {
        user_id: target.kind === "person" ? target.userId : null,
        note: note.trim(),
      });
      onView(next);
      setResetDone(resetDoneWords(target, org.name));
      setNote("");
    } catch (err) {
      setError(limitsErrorWords(err, org.name));
    } finally {
      setConfirm(null);
      setResetting(false);
    }
  };

  return (
    <ConsultantLimitsView
      orgName={org.name}
      view={view}
      form={form}
      onFormChange={(f) => {
        setForm(f);
        setSaved(false);
      }}
      onSave={() => void save()}
      saving={saving}
      saved={saved}
      error={error}
      note={note}
      onNoteChange={setNote}
      confirm={confirm}
      onAskReset={(t) => {
        setConfirm(t);
        setResetDone(null);
      }}
      onConfirmReset={() => void reset()}
      onCancelReset={() => setConfirm(null)}
      resetting={resetting}
      resetDone={resetDone}
      onClose={onClose}
    />
  );
}

/**
 * The sheet: the org's limits (GET), then the panel. Nothing can be saved until they are read. The parent keys it by
 * org. Nothing for anyone but a platform admin.
 */
export function ConsultantLimitsSheet({ org, onClose }: { org: Pick<OrgSummary, "id" | "name"> | null; onClose: () => void }) {
  const { isPlatformAdmin } = useOrg();
  const qc = useQueryClient();
  const limits = useQuery({
    queryKey: copilotLimitsKey(org?.id ?? ""),
    queryFn: () => platformAdminApi.getOrgCopilotLimits(org?.id as string),
    enabled: org !== null && isPlatformAdmin,
    staleTime: 0,
    retry: false,
  });
  if (!org || !isPlatformAdmin) return null;
  return (
    <Sheet open onClose={onClose} ariaLabel={`Consultant limits for ${org.name}`} widthClass="max-w-lg">
      <SheetHeader title="Consultant limits" subtitle={org.name} onClose={onClose} />
      {limits.data !== undefined ? (
        <ConsultantLimitsEditor
          // The editor sets its form once from the limits it opens with; keyed on the server's values, it starts
          // again when another admin changed them since (a refetch), so Save never puts an old value back. The same
          // values keep the key, and a person's edit in progress (#1165 review, LOW 3).
          key={JSON.stringify(formOfLimits(limits.data))}
          org={org}
          view={limits.data}
          onView={(v) => qc.setQueryData(copilotLimitsKey(org.id), v)}
          onClose={onClose}
        />
      ) : limits.isError ? (
        <p role="alert" className="p-5 text-sm text-danger">
          {limitsErrorWords(limits.error, org.name)}
        </p>
      ) : (
        <p className="flex items-center gap-2 p-5 text-sm text-on-surface-muted">
          <Loader2 className="size-4 animate-spin" /> Loading the limits
        </p>
      )}
    </Sheet>
  );
}
